import { Play, FieldType, Zone } from "../api/types";

// Field background images extracted from chlk-ios xcassets.
// Full-field variants are JPEGs (separate asset); middle/endzone are PNGs from xcassets.
// No separate redline variant exists for full-field images.
import fieldCollegeMiddle           from "../../assets/fields/fieldCollegeMiddle.png";
import fieldCollegeMiddleRedline    from "../../assets/fields/fieldCollegeMiddleRedline.png";
import fieldCollegeRedzone          from "../../assets/fields/fieldCollegeRedzone.png";
import fieldCollegeRedlineRedzone   from "../../assets/fields/fieldCollegeRedlineRedzone.png";
import fieldCollegeFull             from "../../assets/fields/fieldCollegeFull.jpg";
import fieldHighSchoolMiddle        from "../../assets/fields/fieldHighSchoolMiddle.png";
import fieldHighSchoolRedzone       from "../../assets/fields/fieldHighSchoolRedzone.png";
import fieldHighSchoolFull          from "../../assets/fields/fieldHighSchoolFull.jpg";
import fieldNflMiddle               from "../../assets/fields/fieldNflMiddle.png";
import fieldNflMiddleRedline        from "../../assets/fields/fieldNflMiddleRedline.png";
import fieldNflRedzone              from "../../assets/fields/fieldNflRedzone.png";
import fieldNflRedlineRedzone       from "../../assets/fields/fieldNflRedlineRedzone.png";
import fieldNflFull                 from "../../assets/fields/fieldNflFull.jpg";

type FieldKey = `${FieldType}_${Zone}_${boolean}`;

const FIELD_MAP: Partial<Record<FieldKey, string>> = {
  "college_middle_false":  fieldCollegeMiddle,
  "college_middle_true":   fieldCollegeMiddleRedline,
  "college_endZone_false": fieldCollegeRedzone,
  "college_endZone_true":  fieldCollegeRedlineRedzone,
  "college_full_false":    fieldCollegeFull,
  "college_full_true":     fieldCollegeFull,   // no separate redline variant for full field

  "high_school_middle_false":  fieldHighSchoolMiddle,
  "high_school_endZone_false": fieldHighSchoolRedzone,
  "high_school_full_false":    fieldHighSchoolFull,
  "high_school_full_true":     fieldHighSchoolFull,

  "nfl_middle_false":  fieldNflMiddle,
  "nfl_middle_true":   fieldNflMiddleRedline,
  "nfl_endZone_false": fieldNflRedzone,
  "nfl_endZone_true":  fieldNflRedlineRedzone,
  "nfl_full_false":    fieldNflFull,
  "nfl_full_true":     fieldNflFull,           // no separate redline variant for full field
};

const fieldImageCache = new Map<string, string>();

async function resolveFieldDataUri(key: FieldKey): Promise<string | null> {
  if (fieldImageCache.has(key)) return fieldImageCache.get(key)!;

  const asset = FIELD_MAP[key];
  if (!asset) return null;

  // webpack asset/inline → already a data URI; otherwise fetch and convert
  if (asset.startsWith("data:")) {
    fieldImageCache.set(key, asset);
    return asset;
  }

  try {
    const resp = await fetch(asset);
    const blob = await resp.blob();
    const dataUri = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
    fieldImageCache.set(key, dataUri);
    return dataUri;
  } catch {
    return null;
  }
}

/** Returns the field background data URI (or null for blank/missing). */
export async function getFieldDataUri(play: Play): Promise<string | null> {
  const { field_type, zone, red_line } = play;
  if (field_type === "blank") return null;
  const key: FieldKey = `${field_type}_${zone}_${red_line}`;
  return resolveFieldDataUri(key);
}

/**
 * Returns an SVG element for the field layer.
 * When fieldDataUri is null the caller will draw the field directly on
 * canvas; we emit a transparent placeholder rect so the SVG coordinates
 * are correct.
 */
export function buildFieldLayer(play: Play, fieldDataUri: string | null): string {
  const { width, height, field_type } = play;

  if (field_type === "blank") {
    return `<rect width="${width}" height="${height}" fill="white"/>`;
  }

  if (!fieldDataUri) {
    // Fallback solid green — used only when image is unavailable
    return `<rect width="${width}" height="${height}" fill="#3a7d44"/>`;
  }

  // Transparent rect — field is drawn directly to canvas (not embedded in SVG)
  return `<rect width="${width}" height="${height}" fill="none"/>`;
}
